import { DS_HEIGHT, DS_WIDTH } from '@calm/evexia-design-tokens';
import { Lottie } from '@rhg/libs/lottie-react';
import soundBars from '@rhg/libs/lottie-react/animations/sound-bars';
import { hexToLottieColor, recolorFills } from '@rhg/libs/lottie-react/utils';
import { Box, SxProps, Theme, useTheme } from '@rhg/libs/material';
import { AnimationPaused } from '@rhg/libs/material/icons';
import { LottieRefCurrentProps } from 'lottie-react';
import { createContext, useContext, useMemo, useRef } from 'react';

const STATIC_FRAME = 25;

/**
 * When true, `PlayingAnimation` holds a single frame instead of looping, so
 * visual snapshots are deterministic. Storybook sets it; the app never does.
 */
export const PlayingAnimationStaticFrameContext = createContext(false);

export type PlayingAnimationSize = 'medium' | 'fill';

export interface PlayingAnimationProps {
    /**
     * Animate while true; show the static paused bars while false. Defaults to
     * true so the existing always-animating call sites are unaffected.
     */
    isPlaying?: boolean;
    /**
     * `default` — theme-contrast bars (white in dark, navy in light), used on
     * the primary play button. `info` — info-blue bars used as the active-row
     * "now playing" indicator (matches the playlist/album Figma).
     */
    color?: 'default' | 'info';
    /**
     * `fill` (default) — stretches to fill the parent (the parent constrains
     * the size). `medium` — fixed 24px square.
     */
    size?: PlayingAnimationSize;
    /**
     * Optional test id. Pass it only where the animation is the dedicated
     * "now playing" indicator so it doesn't collide with the generic
     * play-button icon elsewhere on the page.
     */
    testId?: string;
}

/**
 * Return a deep clone of the shared sound-bars lottie with every bar fill set to
 * `hex`. One asset serves every theme/color variant — the placeholder color in
 * the committed JSON is always overwritten here.
 */
export function buildSoundBars(hex: string): typeof soundBars {
    const clone = JSON.parse(JSON.stringify(soundBars)) as typeof soundBars;
    recolorFills(clone, hexToLottieColor(hex));
    return clone;
}

const styleSizeMedium: SxProps<Theme> = {
    width: DS_WIDTH.BUTTON_XSMALL,
    height: DS_HEIGHT.BUTTON_XSMALL,
    '& svg': { width: '100%', height: '100%' },
};

const styleSizeFill: SxProps<Theme> = {
    width: '100%',
    height: '100%',
    '& svg': { width: '100%', height: '100%' },
};

const SIZE_STYLE: Record<PlayingAnimationSize, SxProps<Theme>> = {
    medium: styleSizeMedium,
    fill: styleSizeFill,
};

const stylePausedIcon: SxProps<Theme> = {
    width: '100%',
    height: '100%',
    color: 'info.main',
};

/**
 * Sound-bar waveform used both as the primary play button's icon and as the
 * active-row "now playing" indicator. Animates while `isPlaying`; when paused it
 * shows the static three-bar `AnimationPaused` icon.
 */
export function PlayingAnimation({
    isPlaying = true,
    color = 'default',
    size = 'fill',
    testId,
}: PlayingAnimationProps) {
    const { palette } = useTheme();

    // One sound-bars asset, recolored per variant from raw DS tokens.
    // so the animated bars match the static paused icon;
    const barHex =
        color === 'info' ? palette.info.main : palette.primary.contrastText;

    const animationData = useMemo(() => buildSoundBars(barHex), [barHex]);
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const isStaticFrame = useContext(PlayingAnimationStaticFrameContext);

    return (
        <Box data-testid={testId} aria-hidden sx={SIZE_STYLE[size]}>
            {isPlaying ? (
                <Lottie
                    lottieRef={lottieRef}
                    autoplay={!isStaticFrame}
                    loop={!isStaticFrame}
                    animationData={animationData}
                    onDOMLoaded={() => {
                        if (isStaticFrame) {
                            lottieRef.current?.goToAndStop(STATIC_FRAME, true);
                        }
                    }}
                />
            ) : (
                <AnimationPaused sx={stylePausedIcon} />
            )}
        </Box>
    );
}
